import { BehaviorSubject, Observable } from 'rxjs';
import { EnumActivityStatus } from '../enums/activity-status.enum';
import { EnumAyaEventType } from '../enums/aya-event-type.enum';
import { EnumRunPhase } from '../enums/run-phase.enum';
import { IActivity } from '../interfaces/activity.interface';
import { IAyaEnvelope } from '../interfaces/aya-stream.interface';

const TOOL_END_STATUS: Record<string, EnumActivityStatus> = {
  [EnumAyaEventType.TOOL_COMPLETED]: EnumActivityStatus.COMPLETED,
  [EnumAyaEventType.TOOL_FAILED]: EnumActivityStatus.FAILED,
  [EnumAyaEventType.TOOL_INTERRUPTED]: EnumActivityStatus.INTERRUPTED
};

const SUBAGENT_STATUS: Record<string, EnumActivityStatus> = {
  [EnumAyaEventType.SUBAGENT_STARTED]: EnumActivityStatus.RUNNING,
  [EnumAyaEventType.SUBAGENT_RUNNING]: EnumActivityStatus.RUNNING,
  [EnumAyaEventType.SUBAGENT_COMPLETED]: EnumActivityStatus.COMPLETED,
  [EnumAyaEventType.SUBAGENT_FAILED]: EnumActivityStatus.FAILED,
  [EnumAyaEventType.SUBAGENT_INTERRUPTED]: EnumActivityStatus.INTERRUPTED,
  [EnumAyaEventType.SUBAGENT_CANCELLED]: EnumActivityStatus.CANCELLED
};

export class ChatActivitiesStore {
  private activities$ = new BehaviorSubject<IActivity[]>([]);
  private phase$ = new BehaviorSubject<EnumRunPhase | null>(null);

  getActivities$(): Observable<IActivity[]> {
    return this.activities$.asObservable();
  }

  getPhase$(): Observable<EnumRunPhase | null> {
    return this.phase$.asObservable();
  }

  /** Retorna `true` quando o envelope alterou a lista ou a fase. */
  apply(envelope: IAyaEnvelope): boolean {
    if (envelope.type === EnumAyaEventType.RUN_PHASE) {
      return this.applyPhase(envelope);
    }
    if (envelope.type === EnumAyaEventType.TOOL_STARTED) {
      return this.startTool(envelope);
    }
    if (TOOL_END_STATUS[envelope.type]) {
      return this.endTool(envelope);
    }
    if (envelope.type === EnumAyaEventType.SUBAGENT_SUPERSEDED) {
      return this.supersedeSubagent(envelope);
    }
    if (SUBAGENT_STATUS[envelope.type]) {
      return this.applySubagent(envelope);
    }
    return false;
  }

  clear(): void {
    this.activities$.next([]);
    this.phase$.next(null);
  }

  private applyPhase(envelope: IAyaEnvelope): boolean {
    const phase = envelope.payload.phase as EnumRunPhase | undefined;
    if (!phase || this.phase$.getValue() === phase) {
      return false;
    }
    this.phase$.next(phase);
    return true;
  }

  /** Identidade é o `tool_call_id`: duas execuções da mesma ferramenta coexistem. */
  private startTool(envelope: IAyaEnvelope): boolean {
    const id = envelope.toolCallId;
    const label = envelope.payload.label as string | undefined;
    if (!id || !label) {
      return false;
    }

    return this.upsert({
      id,
      label,
      status: EnumActivityStatus.RUNNING,
      parentId: (envelope.payload.parent_subagent_id as string) ?? null,
      isBackground: false,
      supersededBy: null
    });
  }

  /** O evento de término costuma vir sem label: o rótulo do início é preservado. */
  private endTool(envelope: IAyaEnvelope): boolean {
    const id = envelope.toolCallId;
    if (!id) {
      return false;
    }
    return this.patch(id, { status: TOOL_END_STATUS[envelope.type] });
  }

  private applySubagent(envelope: IAyaEnvelope): boolean {
    const id = envelope.payload.subagent_id as string | undefined;
    if (!id) {
      return false;
    }

    const isBackground = envelope.payload.background === true;
    const status = SUBAGENT_STATUS[envelope.type];
    const existente = this.find(id);

    if (existente) {
      return this.patch(id, { status, isBackground });
    }

    return this.upsert({
      id,
      label: (envelope.payload.label as string) ?? id,
      status,
      parentId: (envelope.payload.parent_subagent_id as string) ?? null,
      isBackground,
      supersededBy: null
    });
  }

  /** Substituída não é concluída: o sucessor assume, a anterior fica marcada. */
  private supersedeSubagent(envelope: IAyaEnvelope): boolean {
    const id = envelope.payload.subagent_id as string | undefined;
    if (!id) {
      return false;
    }
    return this.patch(id, {
      status: EnumActivityStatus.SUPERSEDED,
      supersededBy:
        (envelope.payload.superseded_by_subagent_id as string) ?? null
    });
  }

  private find(id: string): IActivity | undefined {
    return this.activities$.getValue().find((a) => a.id === id);
  }

  private upsert(activity: IActivity): boolean {
    const atual = this.activities$.getValue();
    const index = atual.findIndex((a) => a.id === activity.id);

    if (index === -1) {
      this.activities$.next([...atual, activity]);
      return true;
    }

    const atualizada = [...atual];
    atualizada[index] = { ...atual[index], ...activity };
    this.activities$.next(atualizada);
    return true;
  }

  private patch(id: string, update: Partial<IActivity>): boolean {
    const atual = this.activities$.getValue();
    const index = atual.findIndex((a) => a.id === id);
    if (index === -1) {
      return false;
    }

    const atualizada = [...atual];
    atualizada[index] = { ...atual[index], ...update };
    this.activities$.next(atualizada);
    return true;
  }
}
